import { _throwNotImplementedException } from './COM';
import { Rs232DisplayAdapter } from './Rs232DisplayAdapter';
import { Rs232Reply } from './Rs232Reply';
import { $ } from '../../../vendor/jquery';
import { objectHasOwnProperty } from '../../Utility';
import _ from 'underscore';

// <editor-fold desc="Enumerations">

/**
 * Enumeration for command type field in ViewSonicCommandType
 * @readonly
 * @enum {Uint8Array}
 */
var ViewSonicCommandType = {
    //GetSerialNo:  // no get serial command for View Sonic
    GetModelName: new Uint8Array([0x67, 0x7a]), // this command actually is a check communication command (no get model command available)
    GetPowerState: new Uint8Array([0x67, 0x6c]),
    SetPowerState: new Uint8Array([0x73, 0x21]),
    SetMuted: new Uint8Array([0x73, 0x36]),
    SetVolume: new Uint8Array([0x73, 0x35]),
    SetVideoInput: new Uint8Array([0x73, 0x22]),
};

/**
 * Enumeration for power state commands
 * @readonly
 * @enum {Uint8Array}
 */
var ViewSonicPowerState = {
    On: new Uint8Array([0x30, 0x30, 0x31]), // '0' - '0' - '1'
    Off: new Uint8Array([0x30, 0x30, 0x30]), // '0' - '0' - '0'
};

/**
 * Enumeration for inputs
 * @readonly
 * @enum {number}
 */
var ViewSonicInput = {
    TV: 0,
    Video1: 1,
    SVideo: 2,
    YPbPr: 3,
    HDMI: 4,
    HDMI2: 14,
    HDMI3: 24,
    DVI: 5,
    VGA: 6,
    VGA2: 16,
    OPS: 7,
    InternalMem: 8,
    DisplayPort: 9,
    parseInt: function (value) {
        for (var prop in ViewSonicInput) {
            if (objectHasOwnProperty(ViewSonicInput, prop) && ViewSonicInput[prop] === value) {
                return prop;
            }
        }

        return 'unknown';
    },
};

/**
 * Enumeration for mute state
 * @readonly
 * @enum {Uint8Array}
 */
var ViewSonicMuteState = {
    Mute: new Uint8Array([0x30, 0x30, 0x31]), // '0' - '0' - '1'
    Unmute: new Uint8Array([0x30, 0x30, 0x30]), // '0' - '0' - '0'
};

// </editor-fold>

// <editor-fold desc="ViewSonicRs232Reply Abstract Class">

/**
 * Generic Rs232Reply for ViewSonic displays, represents a reply packet from the
 * display
 * @class
 * @param {number[]} bytes - the bytes received from the display
 */
function ViewSonicRs232Reply(bytes) {
    Rs232Reply.call(this, bytes);

    if (this.packetString === '2+') {
        // success reply from a power on command (for some models of the view sonic TV)
        // power on command is the exception for how the reply is formatted
        this.message = '+';
        this.messageLength = 2;
        // no way to know the tvID of the tv that was powered on
        return;
    }

    if (bytes.length < 4) {
        throw new Error(
            'invalid packet from View Sonic display: response must be at least 4 bytes'
        );
    }

    this.messageLength = Number(this.packetString.slice(0, 1));

    this.tvID = this.packetString.slice(1, 3);

    this.message = this.packetString.slice(3);
}

ViewSonicRs232Reply.prototype = Object.create(Rs232Reply.prototype);
ViewSonicRs232Reply.prototype.constructor = ViewSonicRs232Reply;

ViewSonicRs232Reply.prototype.verifyPacket = function () {
    if (this.rawPacket.length !== this.messageLength) {
        throw new Error(
            'invalid packet from View Sonic display: response length does not match the length that the response said it should be'
        );
    }
};

ViewSonicRs232Reply.prototype.toString = _throwNotImplementedException(
    'ViewSonicRs232Reply.toString'
);
// </editor-fold>

// <editor-fold desc="ViewSonicGetModelReply Class">
function ViewSonicGetModelReply(bytes) {
    ViewSonicRs232Reply.call(this, bytes);

    if (this.message !== '-' && this.message !== 'rz000') {
        throw 'Com port reply is not that of a View Sonic player';
    }

    //verify the packet integrity
    this.verifyPacket();
}

ViewSonicGetModelReply.prototype = Object.create(ViewSonicRs232Reply.prototype);
ViewSonicGetModelReply.prototype.constructor = ViewSonicGetModelReply;

ViewSonicGetModelReply.prototype.toString = function () {
    return this.message;
};
// </editor-fold>

// <editor-fold desc="ViewSonicSetPowerStateReply Class">
function ViewSonicSetPowerStateReply(bytes) {
    ViewSonicRs232Reply.call(this, bytes);

    //verify the packet integrity
    this.verifyPacket();

    // power command is weird because going from off to on it replies with a '-'
    // which is supposed to mean failure, but the TV always turns on...
    this.success = this.message === '+' || this.message === '-';

    if (this.success) {
        this.resultCode = 'Failed with an unknown error';
    }
}

ViewSonicSetPowerStateReply.prototype = Object.create(ViewSonicRs232Reply.prototype);
ViewSonicSetPowerStateReply.prototype.constructor = ViewSonicSetPowerStateReply;

ViewSonicSetPowerStateReply.prototype.toString = function () {
    return this.success;
};
// </editor-fold>

// <editor-fold desc="ViewSonicGenericReply Class">
function ViewSonicGenericReply(bytes) {
    ViewSonicRs232Reply.call(this, bytes);

    //verify the packet integrity
    this.verifyPacket();

    this.success = this.message === '+';

    if (!this.success) {
        this.resultCode = 'Failed with an unknown error';
    }
}

ViewSonicGenericReply.prototype = Object.create(ViewSonicRs232Reply.prototype);
ViewSonicGenericReply.prototype.constructor = ViewSonicGenericReply;

ViewSonicGenericReply.prototype.toString = function () {
    return this.success;
};
// </editor-fold>

// <editor-fold desc="ViewSonicCommand Class">
function ViewSonicCommand(command, data) {
    ViewSonicPacket.call(this);

    var messageData = new Uint8Array(5);
    messageData.set(command);

    if (!data) {
        data = new Uint8Array([0x30, 0x30, 0x30]); // '000'
    }

    while (data.length < 3) {
        data.unshift(0x30); // pad the front of the data with 0's so that it meets the ViewSonic command requirements
    }

    messageData.set(data, command.length);

    this.message = messageData;
}

ViewSonicCommand.prototype = Object.create(ViewSonicPacket.prototype);
ViewSonicCommand.prototype.constructor = ViewSonicCommand;

// <editor-fold desc="ViewSonicPacket">
function ViewSonicPacket() {
    this.messageLength = 0x38;
    this.tvIDPart1 = 0x30;
    this.tvIDPart12 = 0x31;

    this.message = new Uint8Array(0);

    this.endOfMessage = new Uint8Array([0x0d]); // \r to indicate end of message/packet
}

ViewSonicPacket.prototype.toUint8Array = function () {
    var i;

    var payload = new Uint8Array(4 + this.message.length);

    payload[0] = this.messageLength;
    payload[1] = this.tvIDPart1;
    payload[2] = this.tvIDPart12;

    for (i = 0; i < this.message.length; ++i) {
        payload[3 + i] = this.message[i];
    }

    for (i = 0; i < this.endOfMessage.length; ++i) {
        payload[3 + this.message.length + i] = this.endOfMessage[i];
    }

    return payload;
};
// </editor-fold>

/**
 * Generic ViewSonicRs232DisplayAdapter
 * @class
 * @param {string} displayMake - the vendor name of the hardware display
 * @param {string|null} displayModel - the model of the display
 * @param {Rs232Controller} rs232Controller - controller for interacting with
 * COM port
 */
function ViewSonicRs232DisplayAdapter(displayMake, displayModel, rs232Controller) {
    this.make = 'ViewSonic';

    this.model = displayModel;

    this.controller = rs232Controller;
}

ViewSonicRs232DisplayAdapter.prototype = Object.create(Rs232DisplayAdapter.prototype);
ViewSonicRs232DisplayAdapter.prototype.constructor = ViewSonicRs232DisplayAdapter;

// <editor-fold desc="Rs232DisplayAdapter Methods">

ViewSonicRs232DisplayAdapter.prototype.getModel = function () {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            this.controller.write(
                new ViewSonicCommand(ViewSonicCommandType.GetModelName).toUint8Array()
            );

            this.controller.readLine().then(
                function (response) {
                    try {
                        var result = new ViewSonicGetModelReply(response);

                        jqDeferred.resolve(result);
                    } catch (error2) {
                        jqDeferred.reject(error2);
                    }
                }.bind(this),
                function (error) {
                    jqDeferred.reject(error);
                }
            );
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

// not implementing because view sonic does not have a command to return the
// serial number
//NecRs232DisplayAdapter.prototype.getSerialNumber = function() {};

// will not implement any getter commands yet because all of the commands that
// the user can add via CMS are setter commands
//ViewSonicRs232DisplayAdapter.prototype.getPowerState = function() {};

ViewSonicRs232DisplayAdapter.prototype.setPowerState = function (desiredPowerState) {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            desiredPowerState = desiredPowerState.toLowerCase();
            if (desiredPowerState !== 'on' && desiredPowerState !== 'off') {
                jqDeferred.reject(
                    'invalid requested power state `' +
                        desiredPowerState +
                        '`. must be either `on` or `off`'
                );
            }

            this.controller.write(
                new ViewSonicCommand(
                    ViewSonicCommandType.SetPowerState,
                    desiredPowerState === 'on' ? ViewSonicPowerState.On : ViewSonicPowerState.Off
                ).toUint8Array()
            );

            return Promise.resolve()
                .then(
                    function () {
                        return this.controller.readLine();
                    }.bind(this)
                )
                .then(
                    function (response) {
                        try {
                            var result = new ViewSonicSetPowerStateReply(response);
                        } catch (error2) {
                            jqDeferred.reject(error2);
                            return;
                        }

                        // sometimes the view sonic command reply fails, but the power state
                        // is set, so we want to set the refractory first
                        this.setRefractoryPeriod(1000);

                        if (!result.success) {
                            jqDeferred.reject(
                                'PowerControl ViewSonic command failed with error code `' +
                                    result.resultCode +
                                    '`'
                            );
                            return;
                        }

                        jqDeferred.resolve(result);
                    }.bind(this)
                );
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

ViewSonicRs232DisplayAdapter.prototype.setMuted = function (desiredMuteState) {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            desiredMuteState = desiredMuteState.toLowerCase();
            if (desiredMuteState !== 'mute' && desiredMuteState !== 'unmute') {
                jqDeferred.reject(
                    'invalid requested mute state `' +
                        desiredMuteState +
                        '`. must be either `mute` or `unmute`'
                );
            }

            this.controller.write(
                new ViewSonicCommand(
                    ViewSonicCommandType.SetMuted,
                    desiredMuteState === 'mute'
                        ? ViewSonicMuteState.Mute
                        : ViewSonicMuteState.Unmute
                ).toUint8Array()
            );

            return Promise.resolve()
                .then(
                    function () {
                        return this.controller.readLine();
                    }.bind(this)
                )
                .then(function (response) {
                    var result = new ViewSonicGenericReply(response);

                    if (!result.success) {
                        jqDeferred.reject(
                            'setMuted request failed with error code `' + result.resultCode + '`'
                        );
                    }

                    jqDeferred.resolve(result);
                });
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

ViewSonicRs232DisplayAdapter.prototype.setAudioVolume = function (desiredAudioVolume) {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            desiredAudioVolume = parseInt(desiredAudioVolume);

            return Promise.resolve()
                .then(function () {
                    return { maximumValue: 100 };
                })
                .then(
                    function (getAudioVolumeResponse) {
                        var maximumAudioValue = getAudioVolumeResponse.maximumValue;

                        if (desiredAudioVolume < 0 || desiredAudioVolume > maximumAudioValue) {
                            jqDeferred.reject(
                                'invalid requested volume `' +
                                    desiredAudioVolume +
                                    '`. must be between 0 and ' +
                                    maximumAudioValue
                            );
                        }

                        // convert to command parameters in ASCII
                        desiredAudioVolume = desiredAudioVolume.toString();
                        desiredAudioVolume = ('000' + desiredAudioVolume).substring(
                            desiredAudioVolume.length
                        ); // pad left with 0's
                        desiredAudioVolume = new Uint8Array(
                            _.map(desiredAudioVolume.split(''), function (character) {
                                return character.charCodeAt(0);
                            })
                        );

                        this.controller.write(
                            new ViewSonicCommand(
                                ViewSonicCommandType.SetVolume,
                                desiredAudioVolume
                            ).toUint8Array()
                        );

                        return this.controller.readLine();
                    }.bind(this)
                )
                .then(function (response) {
                    var result = new ViewSonicGenericReply(response);

                    if (!result.success) {
                        jqDeferred.reject(
                            'setAudioVolume request failed with error code `' +
                                result.resultCode +
                                '`'
                        );
                    }

                    jqDeferred.resolve(result);
                });
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

ViewSonicRs232DisplayAdapter.prototype.setVideoInput = function (desiredVideoInput) {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            var originalReadTimeout;

            return Promise.resolve()
                .then(
                    function () {
                        if (!ViewSonicInput[desiredVideoInput]) {
                            jqDeferred.reject(
                                'invalid requested input `' +
                                    desiredVideoInput +
                                    '`. Must be one of ViewSonicInput enumeration.'
                            );
                        }

                        //encode desiredAudioVolume as ASCII characters
                        desiredVideoInput = ViewSonicInput[desiredVideoInput].toString();
                        desiredVideoInput = ('000' + desiredVideoInput).substring(
                            desiredVideoInput.length
                        );
                        desiredVideoInput = new Uint8Array(
                            _.map(desiredVideoInput.split(''), function (character) {
                                return character.charCodeAt(0);
                            })
                        );

                        this.controller.write(
                            new ViewSonicCommand(
                                ViewSonicCommandType.SetVideoInput,
                                desiredVideoInput
                            ).toUint8Array()
                        );

                        //NOTE: this command can take quite some time if switching to VGA
                        originalReadTimeout = this.controller.readTimeout;
                        this.controller.readTimeout = 5000;

                        return this.controller.readLine();
                    }.bind(this)
                )
                .catch(
                    function (e) {
                        this.controller.readTimeout = originalReadTimeout;

                        jqDeferred.reject(e.toString());
                    }.bind(this)
                )
                .then(
                    function (response) {
                        this.controller.readTimeout = originalReadTimeout;

                        var result = new ViewSonicGenericReply(response);

                        if (!result.success) {
                            jqDeferred.reject(
                                'setVideoInput request failed with error code `' +
                                    result.resultCode +
                                    '`'
                            );
                        }

                        this.setRefractoryPeriod(7000);

                        jqDeferred.resolve(result);
                    }.bind(this)
                );
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

// </editor-fold>

// does not deviate from the default, this is just here to serve as an example
// as this functionality is not yet used anywhere: change the com port settings
// prior to connection when using the view sonic adaptor
ViewSonicRs232DisplayAdapter.comSettings = {
    baudRate: 9600,
};

//tell the Rs232DisplayAdapter service about the "ViewSonic" brand of displays
Rs232DisplayAdapter.registerVendorAdapter('viewsonic', ViewSonicRs232DisplayAdapter);

export { ViewSonicRs232DisplayAdapter };
